'use client';
import React, { useEffect, useRef, useState } from 'react';

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  animation?: 'fade-in-up' | 'fade-in' | 'slide-in-left' | 'slide-in-right';
  delay?: number;
  threshold?: number;
}

export default function AnimatedSection({
  children,
  className = '',
  animation = 'fade-in-up',
  delay = 0,
  threshold = 0.1,
}: AnimatedSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  const animationClasses = {
    'fade-in-up': isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-10',
    'fade-in': isVisible ? 'animate-fade-in' : 'opacity-0',
    'slide-in-left': isVisible ? 'animate-slide-in-left' : 'opacity-0 -translate-x-12',
    'slide-in-right': isVisible ? 'animate-slide-in-right' : 'opacity-0 translate-x-12',
  };

  return (
    <div data-component-id="src/components/ui/AnimatedSection.tsx:48:4" data-component-path="src/components/ui/AnimatedSection.tsx" data-component-line="48" data-component-end-line="54" data-component-file="AnimatedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={ref}
      className={`transition-all duration-700 ${animationClasses[animation]} ${className}`}
      style={{ animationDelay: `${delay}ms`, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
